import * as format from '../utils/formatters';
import { TFunction } from 'i18next';

export const MAX_INSIGHTS_ROWS = 10000;

type yAxisMapType = {
    [key: string]: {
        name: string;
        yAxisLabel?: string;
        icon: string;
        type:
            | 'currency'
            | 'date'
            | 'percentage'
            | 'decimal'
            | 'category'
            | 'doubleDecimal'
            | 'sec'
            | 'decimalStacked';
        format: (value: any) => string;
        formatType?: string;
        chartFormat?: (value: any) => string;
    };
};

type configByGroupTypeType = {
    [key: string]: {
        type: string;
        name: string;
        propName: string;
        xAxisType: string;
    };
};

export const getYAxisMap = ({
    currency,
    t,
}: {
    currency: string;
    t: TFunction;
}): yAxisMapType => {
    return {
        exposures: {
            name: t('measurement:dataExplorer.metrics.exposures'),
            icon: 'images',
            type: 'decimal',
            format: format.decimal,
            chartFormat: format.abbreviatedDecimal,
        },
        duration: {
            name: t('measurement:dataExplorer.metrics.duration'),
            icon: 'clock',
            type: 'sec',
            format: format.decimal,
            chartFormat: format.abbreviatedDecimal,
        },
        impressions: {
            name: t('measurement:dataExplorer.metrics.impressions'),
            icon: 'eye',
            type: 'decimal',
            format: format.decimal,
            chartFormat: format.abbreviatedDecimal,
        },
        videoViews: {
            name: t('measurement:dataExplorer.metrics.videoViews'),
            icon: 'video',
            type: 'decimal',
            format: format.decimal,
            chartFormat: format.abbreviatedDecimal,
        },
        engagements: {
            name: t('measurement:dataExplorer.metrics.engagements'),
            icon: 'thumbs-up',
            type: 'decimal',
            format: format.decimal,
            chartFormat: format.abbreviatedDecimal,
        },
        fullMediaValue: {
            name: t('measurement:dataExplorer.metrics.fullMediaValue'),
            yAxisLabel: t('measurement:dataExplorer.columns.fullMediaValue'),
            icon: 'dollar-sign',
            type: 'currency',
            format: v => format.currency(v, { currency }),
            chartFormat: v => format.shortCurrency(v, 0, currency),
        },
        averageMvp: {
            name: t('measurement:dataExplorer.metrics.averageMvp'),
            icon: 'percent',
            type: 'percentage',
            format: format.percentage,
        },
        sponsorMediaValue: {
            name: t('measurement:dataExplorer.metrics.sponsorMediaValue'),
            yAxisLabel: t('measurement:dataExplorer.columns.sponsorMediaValue'),
            icon: 'hand-holding-usd',
            type: 'currency',
            format: v => format.currency(v, { currency }),
            chartFormat: v => format.shortCurrency(v, 0, currency),
        },
        postCount: {
            name: t('measurement:dataExplorer.metrics.postCount'),
            icon: 'comment',
            type: 'decimal',
            format: format.decimal,
            chartFormat: format.abbreviatedDecimal,
        },
        broadcastCount: {
            name: t('measurement:dataExplorer.metrics.broadcastCount'),
            icon: 'broadcast-tower',
            type: 'decimal',
            format: format.decimal,
            chartFormat: format.abbreviatedDecimal,
        },
    };
};

export const getConfigByGroupType = (t: TFunction): configByGroupTypeType => ({
    accountOwnershipType: {
        type: 'bar',
        name: t(
            'measurement:dataExplorer.groupByCategories.accountOwnershipTypes',
        ),
        propName: 'accountOwnershipTypeName',
        xAxisType: 'category',
    },
    collection: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.collections'),
        propName: 'collectionName',
        xAxisType: 'category',
    },
    placementType: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.placementTypes'),
        propName: 'placementTypeName',
        xAxisType: 'category',
    },
    date: {
        type: 'line',
        name: t('measurement:dataExplorer.groupByCategories.dates'),
        propName: 'date',
        xAxisType: 'date',
    },
    postType: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.postTypes'),
        propName: 'postType',
        xAxisType: 'category',
    },
    mediaType: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.mediaTypes'),
        propName: 'mediaTypeName',
        xAxisType: 'category',
    },
    rightsholder: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.rightsholders'),
        propName: 'rightsholderName',
        xAxisType: 'category',
    },
    rightsholderPlacement: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.placements'),
        propName: 'rightsholderPlacementName',
        xAxisType: 'category',
    },
    socialPlatform: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.platforms'),
        propName: 'socialPlatformName',
        xAxisType: 'category',
    },
    sponsor: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.sponsors'),
        propName: 'sponsorName',
        xAxisType: 'category',
    },
    account: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.account'),
        propName: 'accountUsername',
        xAxisType: 'category',
    },
    mediaUploadName: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.broadcast'),
        propName: 'mediaUploadName',
        xAxisType: 'category',
    },
    gameName: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.event'),
        propName: 'gameName',
        xAxisType: 'category',
    },
    socialAccountGroup: {
        type: 'bar',
        name: t(
            'measurement:dataExplorer.groupByCategories.socialAccountGroup',
        ),
        propName: 'socialAccountGroupName',
        xAxisType: 'category',
    },
    country: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.broadcastCountry'),
        propName: 'countryName',
        xAxisType: 'category',
    },
    seasonType: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.seasonType'),
        propName: 'seasonTypeName',
        xAxisType: 'category',
    },
    gameType: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.gameType'),
        propName: 'gameTypeName',
        xAxisType: 'category',
    },
    homeTeam: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.homeTeam'),
        propName: 'homeTeamName',
        xAxisType: 'category',
    },
    awayTeam: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.awayTeam'),
        propName: 'awayTeamName',
        xAxisType: 'category',
    },
    authorshipType: {
        type: 'bar',
        name: t('measurement:dataExplorer.groupByCategories.authorshipType'),
        propName: 'authorshipTypeName',
        xAxisType: 'category',
    },
    collaboratingAccounts: {
        type: 'bar',
        name: t(
            'measurement:dataExplorer.groupByCategories.collaboratingAccounts',
        ),
        propName: 'collaboratingAccounts',
        xAxisType: 'category',
    },
});

export const SORTING_ORDER = {
    DESC: 1,
    ASC: -1,
};

export const METRIC_TYPES = {
    exposures: 'exposures',
    duration: 'duration',
    impressions: 'impressions',
    videoViews: 'videoViews',
    engagements: 'engagements',
    fullMediaValue: 'fullMediaValue',
    averageMvp: 'averageMvp',
    sponsorMediaValue: 'sponsorMediaValue',
    postCount: 'postCount',
    broadcastCount: 'broadcastCount',
};

export const getDataExplorerTabs = (t: TFunction) => ({
    TABLE: t('measurement:dataExplorer.tabs.dataTable'),
    VISUALIZATION: t('measurement:dataExplorer.tabs.visualization'),
});
